'use client'

import { useState } from 'react'
import { DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { useCurrentUser } from '@/hooks/use-current-user'
import {
  AlertTriangle,
  HelpCircle,
  Mail,
  Loader2Icon,
  AlertCircle,
} from 'lucide-react'
import { logoutAccount } from '@/lib/db/user'
import { popModal } from '@/components/modals'
import { cn } from '@/lib/utils'

export default function DeleteAccountConfirmModal() {
  const t = useTranslations('modals.delete_account')
  const { data: user } = useCurrentUser()
  const [confirmationText, setConfirmationText] = useState('')
  const [email, setEmail] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleDelete = async () => {
    if (confirmationText !== 'DELETE ACCOUNT' || email !== user?.email) {
      setErrorMessage(t('invalid_confirmation'))
      return
    }

    setIsDeleting(true)
    try {
      const response = await fetch('/api/me/delete-account', {
        method: 'POST',
      })

      if (!response.ok) {
        if (response.status === 400) {
          throw new Error(
            'You have an active subscription, please cancel it first',
          )
        }
        throw new Error('Failed to delete account')
      }

      toast.success(t('success'))
      await logoutAccount()
      // Redirect to home page after successful deletion
      window.location.href = '/'
    } catch (error: any) {
      setErrorMessage(error.message)
      setIsDeleting(false)
    }
  }

  const closeErrorPopup = () => {
    setErrorMessage(null)
  }

  const handleClose = () => {
    popModal()
  }

  // Error popup
  if (errorMessage) {
    return (
      <DialogContent className="sm:max-w-[480px]">
        <div className="flex flex-col items-center py-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold mb-2">{t('error.title')}</h2>
          <p className="text-center mb-4">{errorMessage}</p>
          <div className="flex w-full gap-4">
            <Button
              variant="outline_2"
              className="flex-1"
              onClick={closeErrorPopup}
            >
              {t('error.cancel_button')}
            </Button>
            <Button className="flex-1 " onClick={closeErrorPopup}>
              {t('error.try_again_button')}
            </Button>
          </div>
        </div>
      </DialogContent>
    )
  }

  return (
    <DialogContent
      className={cn(
        'sm:max-w-[640px] max-w-[95vw] p-0 gap-0 overflow-hidden',
        'bg-[#1a1a1a] border-2 border-red-500/50',
        'shadow-[0_0_40px_rgba(239,68,68,0.3)]',
      )}
      overlayClassName="bg-black/90 backdrop-blur-sm"
    >
      {/* Warning Header with Animated Background */}
      <div
        className={cn(
          'relative px-6 pt-6 pb-5',
          'bg-gradient-to-b from-red-500/20 via-red-500/10 to-transparent',
          'border-b-2 border-red-500/30',
        )}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center border-2 border-red-500/50">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-2xl font-bold text-white mb-2">
              {t('title')}
            </DialogTitle>
            <p className="text-lg font-semibold text-red-400">{t('warning')}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 space-y-6 max-h-[70vh] overflow-y-auto">
        {/* Critical Warning Box */}
        <div
          className={cn(
            'p-5 rounded-lg',
            'bg-red-500/10 border-2 border-red-500/40',
            'space-y-4',
          )}
        >
          <p className="text-base font-semibold text-red-300">
            {t('critical_warning_title')}
          </p>
          <ul className="space-y-2.5 list-none">
            <li className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <span className="text-sm text-white/90">{t('warning_1')}</span>
            </li>
            <li className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <span className="text-sm text-white/90">{t('warning_2')}</span>
            </li>
            <li className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <span className="text-sm text-white/90">{t('warning_3')}</span>
            </li>
            <li className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <span className="text-sm text-white/90">{t('warning_4')}</span>
            </li>
          </ul>
        </div>

        {/* Alternative Solutions */}
        <div
          className={cn(
            'p-5 rounded-lg',
            'bg-blue-500/10 border border-blue-500/30',
            'space-y-3',
          )}
        >
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-sm font-semibold text-blue-300">
                {t('before_delete_title')}
              </p>
              <p className="text-xs text-white/70 leading-relaxed">
                {t('before_delete_description')}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Mail className="w-4 h-4 text-blue-400" />
                <a
                  href="mailto:support@heyboss.ai"
                  className="text-sm text-blue-400 hover:text-blue-300 underline"
                >
                  {t('contact_support')}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Instructions */}
        <div className="space-y-1">
          <p className="text-sm font-medium text-white/90">
            {t('confirmation_instruction')}
          </p>
          <p className="text-xs text-white/60">
            {t('confirmation_subinstruction')}
          </p>
        </div>

        {/* Confirmation Inputs */}
        <div className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="confirmation-text"
              className="text-sm font-medium text-white/90 flex items-center gap-2"
            >
              <span>{t('type_delete_account')}</span>
            </label>
            <Input
              id="confirmation-text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={t('delete_account_placeholder')}
              className={cn(
                'h-12 bg-[#0f0f0f] border-red-500/30 text-white',
                'placeholder:text-white/40',
                'focus:border-red-500 focus:ring-2 focus:ring-red-500/20',
                'transition-all duration-200',
              )}
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="email"
              className="text-sm font-medium text-white/90 flex items-center gap-2"
            >
              <span>{t('enter_email')}</span>
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={user?.email}
              className={cn(
                'h-12 bg-[#0f0f0f] border-red-500/30 text-white',
                'placeholder:text-white/40',
                'focus:border-red-500 focus:ring-2 focus:ring-red-500/20',
                'transition-all duration-200',
              )}
            />
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div
        className={cn(
          'px-6 py-4 border-t border-red-500/20',
          'bg-gradient-to-t from-red-500/5 to-transparent',
          'flex items-center justify-between gap-3',
        )}
      >
        <Button
          variant="outline"
          onClick={handleClose}
          className={cn(
            'h-11 px-6',
            'bg-[#1a1a1a] border-white/20 text-white',
            'hover:bg-white/10 hover:border-white/30 hover:text-white',
            'transition-all duration-200',
          )}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleDelete}
          disabled={
            isDeleting ||
            confirmationText !== 'DELETE ACCOUNT' ||
            email !== user?.email
          }
          className={cn(
            'h-11 px-6',
            'bg-red-600 hover:bg-red-700 text-white font-semibold',
            'border border-red-600 hover:border-red-700',
            'shadow-[0_0_20px_-5px_rgba(239,68,68,0.4)]',
            'hover:shadow-[0_0_30px_-5px_rgba(239,68,68,0.6)]',
            'transition-all duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600',
          )}
        >
          {isDeleting ? (
            <>
              <Loader2Icon className="w-4 h-4 animate-spin mr-2" />
              {t('deleting')}
            </>
          ) : (
            t('confirm_delete')
          )}
        </Button>
      </div>
    </DialogContent>
  )
}
